import React, { useState } from 'react';
import {
  Box,
  Grid,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Container,
  Text,
  IconButton,
  Flex,
  AspectRatio,
  GridItem,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

const CaseStudyImageGallery = ({ 
  images = [], 
  title = "Project Gallery", 
  description = "Explore the visual journey of our project"
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Ensure we have proper image objects with required properties
  // Handle both new multiple images schema and legacy single image format
  const galleryImages = images.filter(img => img?.url).map(img => ({
        url: img.url,
        alt: img.alt || "Gallery Image",
        caption: img.caption || "Project showcase"
      }));

  const openModal = (index) => {
    setSelectedImageIndex(index);
    onOpen();
  };

  const nextImage = () => {
    setSelectedImageIndex((prevIndex) => 
      (prevIndex + 1) % galleryImages.length
    );
  };

  const prevImage = () => {
    setSelectedImageIndex((prevIndex) => 
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    );
  };

  // Always render with either API images or fallback sample images
  if (galleryImages.length === 0) {
    return null;
  }

  // Show the first 4 images maximum (1 full width + 3 in row)
  // If fewer than 4 images, show what's available
  const displayImages = galleryImages.slice(0, 4);
  const hasMainImage = displayImages.length >= 1;
  const secondaryImages = displayImages.slice(1);

  return (
    <Container maxW="7xl" >
      <Grid 
        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
        templateRows={{ base: "auto", md: "auto auto" }}
        gap={{ base: 3, md: 4, lg: 6 }}
        maxW="7xl"
        mx="auto"
      >
        {/* Next 3 Images - Row Layout */}
        {secondaryImages.map((image, index) => (
          <GridItem 
            key={index + 1}
            cursor="pointer"
            onClick={() => openModal(index + 1)}
            position="relative"
            overflow="hidden"
            borderRadius="lg"
            boxShadow="md"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "xl",
            }}
          >
            <AspectRatio ratio={4/3}>
              <Image
                src={image.url}
                alt={image.alt}
                objectFit="cover"
                w="full"
                h="full"
                transition="transform 0.3s ease"
                _hover={{
                  transform: "scale(1.05)",
                }}
              />
            </AspectRatio>
            
            {/* Overlay on hover */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.400"
              opacity={0}
              transition="opacity 0.3s ease"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ opacity: 1 }}
            >
              <Text
                color="white"
                fontSize="sm"
                fontWeight="semibold"
                textAlign="center"
                px={4}
              >
                Click to view
              </Text>
            </Box>
          </GridItem>
        ))}
        {/* {hasMainImage && (
          <GridItem 
            colSpan={{ base: 1, md: 3 }}
            cursor="pointer"
            onClick={() => openModal(0)}
            position="relative"
            overflow="hidden"
            borderRadius="xl"
            boxShadow="lg"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px)",
              boxShadow: "2xl",
            }}
          >
            <AspectRatio ratio={{ base: 16/11, md: 2.5/1, lg: 21/14 }}>
              <Image
                src={displayImages[0].url}
                alt={displayImages[0].alt}
                objectFit="cover"
                w="full"
                h="full"
                transition="transform 0.3s ease"
                _hover={{
                  transform: "scale(1.02)",
                }}
              />
            </AspectRatio>
            
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.400"
              opacity={0}
              transition="opacity 0.3s ease"
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ opacity: 1 }}
            >
              <Text
                color="white"
                fontSize="lg"
                fontWeight="semibold"
                textAlign="center"
                px={4}
              >
                Click to view gallery
              </Text>
            </Box>
          </GridItem>
        )} */}
      </Grid>

      {/* Show additional images count if more than 4 */}
      {galleryImages.length > 4 && (
        <Box textAlign="center" mt={6}>
          <Text color="gray.600" fontSize="sm">
            +{galleryImages.length - 4} more images in gallery
          </Text>
        </Box>
      )}

      {/* Modal for image preview */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
          <ModalCloseButton 
            color="white" 
            size="lg" 
            top={4} 
            right={4}
            zIndex={2}
          />
          <ModalBody p={0} position="relative">
            <Flex align="center" justify="center" h="full">
              {/* Previous button */}
              <IconButton
                icon={<ChevronLeftIcon />}
                position="absolute"
                left={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                colorScheme="whiteAlpha"
                variant="solid"
                size="lg"
                onClick={prevImage}
                isDisabled={galleryImages.length <= 1}
              />

              {/* Main image */}
              <Box textAlign="center" maxW="full" maxH="full">
                <Image
                  src={galleryImages[selectedImageIndex]?.url}
                  alt={galleryImages[selectedImageIndex]?.alt}
                  maxW="full"
                  maxH="80vh"
                  objectFit="contain"
                  borderRadius="lg"
                />
                
                {/* Image caption */}
                <Text
                  color="white"
                  mt={4}
                  fontSize="lg"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  {galleryImages[selectedImageIndex]?.caption}
                </Text>
                
                {/* Image counter */}
                <Text
                  color="gray.300"
                  mt={2}
                  fontSize="sm"
                  textAlign="center"
                >
                  {selectedImageIndex + 1} of {galleryImages.length}
                </Text>
              </Box>

              {/* Next button */}
              <IconButton
                icon={<ChevronRightIcon />}
                position="absolute"
                right={4}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                colorScheme="whiteAlpha"
                variant="solid"
                size="lg"
                onClick={nextImage}
                isDisabled={galleryImages.length <= 1}
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CaseStudyImageGallery;